// @ts-check
/* BNPL page (/bnpl/): search and filter 0% instalment plans.

   The page ships with the first products already in the HTML (written by
   scripts/seo/generate_seo_pages.py, so search engines see them). This script
   loads the full list (data/bnpl-products.json, built by
   scripts/instalments/refresh_instalments.py) and takes over the list, the
   three dropdowns and the search box. Filters live in the URL
   (?bank=alfalah&cat=Phones&q=iphone&sort=price) so badge links and shared
   links land pre-filtered. Only 0% options exist in the data. */
(function () {
  "use strict";

  const root = document.getElementById("bnpl-app");
  if (!root) return;

  const PAGE = 24;
  const KEY_TENURES = [3, 6, 12, 24];
  const SORTS = ["longest", "price", "monthly"];
  const listEl = /** @type {HTMLElement} */ (document.getElementById("bnpl-list"));
  const pagerEl = /** @type {HTMLElement} */ (document.getElementById("bnpl-pager"));
  const searchEl = /** @type {HTMLInputElement} */ (document.getElementById("bnpl-search"));
  const bankEl = /** @type {HTMLSelectElement} */ (document.getElementById("bnpl-bank"));
  const catEl = /** @type {HTMLSelectElement} */ (document.getElementById("bnpl-cat"));
  const sortEl = /** @type {HTMLSelectElement} */ (document.getElementById("bnpl-sort"));

  const params = new URLSearchParams(location.search);
  const f = {
    q: params.get("q") || "",
    bank: params.get("bank") || "all",
    category: params.get("cat") || "all",
    sort: SORTS.includes(params.get("sort") || "") ? /** @type {string} */ (params.get("sort")) : "longest",
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
  };
  /** @type {any} */
  let data = null;

  const pkr = (n) => "PKR " + Math.round(n).toLocaleString("en-US");
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const shortDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };
  const track = (name, props) => { try { window.posthog?.capture?.(name, props); } catch (_) {} };

  // Cards saved in My Wallet on the main tool: { bankId: "Card name" }.
  function ownedCards() {
    const out = {};
    try {
      const saved = JSON.parse(localStorage.getItem("konsacard_state_v1") || "{}");
      (saved.ownedCards || []).forEach((key) => {
        const [bank, card] = String(key).split(" || ");
        const plan = (data.banks || []).find((b) => b.bank === bank);
        if (!plan || !/credit/i.test(card || "")) return;
        if (plan.exclude && new RegExp(plan.exclude, "i").test(card)) return;
        if (!out[plan.id]) out[plan.id] = card;
      });
    } catch (_) {}
    return out;
  }

  function feeText(brand, months, price) {
    const fees = brand.fees || {};
    if (fees[String(months)] === undefined) return "";
    const pct = Number(fees[String(months)]);
    return pct === 0 ? "No fee" : `Fee ${pkr((price * pct) / 100)} + FED`;
  }

  function visibleOptions(options) {
    if (options.length <= 5) return options;
    const last = options[options.length - 1];
    const keep = options.filter(([m]) => KEY_TENURES.includes(m));
    return keep.includes(last) ? keep : [...keep, last];
  }

  function itemHtml(p, owned) {
    const brand = data.brands[p[0]];
    const bank = data.banks.find((b) => b.id === brand.bank) || {};
    const bankName = brand.bank === "mcb" ? "MCB" : bank.bank;
    const hasFees = Object.keys(brand.fees || {}).length > 0;
    const shown = visibleOptions(p[3]);
    const rows = shown.map(([m, monthly]) =>
      `<tr><td>${m} months</td><td><strong>${esc(pkr(monthly))}</strong>/month</td>${hasFees ? `<td class="bnpl-fee">${esc(feeText(brand, m, p[2]))}</td>` : ""}</tr>`).join("");
    const extra = p[3].filter(([m]) => !shown.some(([v]) => v === m)).map(([m]) => m);
    const foot = [bank.booking, brand.region, brand.date ? `List dated ${shortDate(brand.date)}` : ""].filter(Boolean).map(esc).join(" · ");
    const source = brand.url || bank.pageUrl;
    return `<article class="bnpl-item">
      <div class="bnpl-head">
        <div class="bnpl-title"><div class="bnpl-name">${esc(p[1])}</div><div class="bnpl-meta">${esc(brand.name)} · ${esc(p[4])}</div></div>
        <div class="bnpl-price">${esc(pkr(p[2]))}</div>
      </div>
      <div class="bnpl-bank">${esc(bankName)} credit cards</div>
      <table class="bnpl-opts"><tbody>${rows}</tbody></table>
      ${extra.length ? `<div class="bnpl-extra">Also ${extra.join(", ")} months at 0%</div>` : ""}
      ${hasFees ? "" : `<div class="bnpl-extra">Processing fee per ${esc(bankName)}'s schedule of charges</div>`}
      ${owned[brand.bank] ? `<div class="bnpl-owned"><span class="ph-i" style="--ph:url(/assets/icons/ph/check.svg)" aria-hidden="true"></span> Your ${esc(owned[brand.bank])} qualifies</div>` : ""}
      <div class="bnpl-foot">${foot}${source ? ` · <a href="${esc(source)}" target="_blank" rel="noopener noreferrer">Bank's list</a>` : ""}</div>
    </article>`;
  }

  function filtered() {
    const terms = f.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const base = data.products.filter((p) => {
      if (f.bank !== "all" && data.brands[p[0]].bank !== f.bank) return false;
      if (!terms.length) return true;
      const hay = `${p[1]} ${data.brands[p[0]].name} ${p[4]}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
    const list = base.filter((p) => f.category === "all" || p[4] === f.category);
    const longest = (p) => p[3][p[3].length - 1][0];
    const lowestMonthly = (p) => Math.min(...p[3].map((o) => o[1]));
    const sorters = {
      longest: (a, b) => longest(b) - longest(a) || a[2] - b[2],
      price: (a, b) => a[2] - b[2],
      monthly: (a, b) => lowestMonthly(a) - lowestMonthly(b),
    };
    list.sort(sorters[f.sort]);
    return { list, base };
  }

  function syncUrl() {
    const next = new URLSearchParams();
    if (f.q.trim()) next.set("q", f.q.trim());
    if (f.bank !== "all") next.set("bank", f.bank);
    if (f.category !== "all") next.set("cat", f.category);
    if (f.sort !== "longest") next.set("sort", f.sort);
    if (f.page > 1) next.set("page", String(f.page));
    const qs = next.toString();
    history.replaceState(null, "", qs ? `${location.pathname}?${qs}` : location.pathname);
  }

  function render() {
    const { list, base } = filtered();
    const counts = {};
    base.forEach((p) => { counts[p[4]] = (counts[p[4]] || 0) + 1; });
    catEl.innerHTML = [["all", "All categories", base.length], ...data.categories.map((c) => [c, c, counts[c] || 0])]
      .filter(([value, , n], i) => i === 0 || n > 0 || value === f.category)
      .map(([value, label, n]) => `<option value="${esc(value)}"${f.category === value ? " selected" : ""}>${esc(label)} (${n.toLocaleString("en-US")})</option>`)
      .join("");
    const owned = ownedCards();
    const pages = Math.max(1, Math.ceil(list.length / PAGE));
    f.page = Math.min(f.page, pages);
    const start = (f.page - 1) * PAGE;
    const shown = list.slice(start, start + PAGE);
    listEl.innerHTML = shown.length
      ? shown.map((p) => itemHtml(p, owned)).join("")
      : `<p class="bnpl-empty">No listed products match. Try a shorter search or another category.</p>`;
    renderPager(pages, start, shown.length, list.length);
    syncUrl();
  }

  // Same pager as the tables on other content pages (components.css .pagination).
  function pageRange(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, "…", total];
    if (current >= total - 3) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "…", current - 1, current, current + 1, "…", total];
  }

  function renderPager(pages, start, count, total) {
    if (pages <= 1) { pagerEl.innerHTML = ""; return; }
    const btn = (label, page, { disabled = false, active = false, aria = `Page ${page}` } = {}) =>
      `<button type="button" class="pg-btn${active ? " active" : ""}" data-page="${page}" aria-label="${esc(aria)}"${active ? ' aria-current="page"' : ""}${disabled ? " disabled" : ""}>${label}</button>`;
    pagerEl.innerHTML = [
      btn("←", f.page - 1, { disabled: f.page === 1, aria: "Previous page" }),
      ...pageRange(f.page, pages).map((item) => item === "…" ? `<span class="pg-ellipsis">…</span>` : btn(String(item), item, { active: item === f.page })),
      btn("→", f.page + 1, { disabled: f.page === pages, aria: "Next page" }),
      `<span class="pg-info">${(start + 1).toLocaleString("en-US")}–${(start + count).toLocaleString("en-US")} of ${total.toLocaleString("en-US")}</span>`,
    ].join("");
  }

  function bind() {
    searchEl.value = f.q;
    bankEl.value = data.banks.some((b) => b.id === f.bank) ? f.bank : "all";
    f.bank = bankEl.value;
    sortEl.value = f.sort;
    let timer = 0;
    searchEl.addEventListener("input", () => {
      f.q = searchEl.value;
      f.page = 1;
      render();
      clearTimeout(timer);
      timer = window.setTimeout(() => { if (f.q.trim()) track("bnpl_search", { length: f.q.trim().length }); }, 1200);
    });
    bankEl.addEventListener("change", () => { f.bank = bankEl.value; f.page = 1; render(); });
    catEl.addEventListener("change", () => { f.category = catEl.value; f.page = 1; render(); });
    sortEl.addEventListener("change", () => { f.sort = sortEl.value; f.page = 1; render(); });
    pagerEl.addEventListener("click", (e) => {
      const b = /** @type {HTMLElement} */ (e.target).closest("[data-page]");
      if (!b || b.hasAttribute("disabled")) return;
      f.page = Number(b.getAttribute("data-page"));
      render();
      // Back to the top of the list, not the top of the page.
      const top = root.querySelector(".bnpl-controls");
      if (top) window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - 12, behavior: "smooth" });
    });
  }

  const version = root.dataset.version ? `?v=${encodeURIComponent(root.dataset.version)}` : "";
  fetch(`/data/bnpl-products.json${version}`)
    .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
    .then((json) => {
      data = json;
      bind();
      render();
      root.classList.add("bnpl-ready");
      track("bnpl_open", { bank: f.bank, from: document.referrer ? "link" : "direct" });
    })
    .catch(() => {
      // The products already in the HTML stay usable; only filtering is lost.
      root.classList.add("bnpl-offline");
    });
})();
